import { useMemo } from 'react'
import { IFilterSet } from '../..'
import { ENTITY_TYPES } from '@sales-center/common'
import {
  useGetMainLabel,
  useGetModeLabel,
  useGetSubLabel,
} from '../../utils/hooks'
import { useFormikContext } from 'formik'
import { useAppSelector } from '@user/store/hooks/useAppSelector'
import {
  KNOCK_STATUS_FILTER_NAME,
  knockStatusesToOptions,
} from '@user/store/knockStatus/helpers'
import { selectAllKnockStatuses } from '@user/store/knockStatus/slice'
import { resolveOptionLabels } from '../../utils'
import { IMultiSelectFilter, MultiSelect } from '../../typings'

export const useData = <U, T extends IFilterSet<U>>(
  filterId: string,
  entityType?: ENTITY_TYPES,
  salesTeamCount?: number,
) => {
  const { values: filterSet } = useFormikContext<T>()

  const knockStatuses = useAppSelector(selectAllKnockStatuses)

  const [typeName, rawValue] = useMemo(() => {
    const filter = filterSet.filters[filterId]
    return [filter.type, filter.value]
  }, [filterSet, filterId])

  // The chip label is built from the `{ value, label }` snapshot stored in the
  // filter set, so a knock status renamed after it was selected would keep
  // showing its old name here. Read the label off the live catalog instead.
  const value = useMemo(() => {
    if (filterId !== KNOCK_STATUS_FILTER_NAME || typeName !== 'multiSelect')
      return rawValue

    const multiSelect = rawValue as MultiSelect
    const options = resolveOptionLabels(
      multiSelect.options,
      knockStatusesToOptions(knockStatuses),
    )

    return options === multiSelect.options
      ? rawValue
      : { ...multiSelect, options }
  }, [filterId, typeName, rawValue, knockStatuses])

  const subLabel = useGetSubLabel(
    typeName,
    value,
    entityType,
    filterId,
    salesTeamCount,
  )
  const mainLabel = useGetMainLabel(filterId, entityType)
  const modeLabel = useGetModeLabel(
    typeName,
    value,
    (filterSet.filters[filterId] as IMultiSelectFilter<U>).hideModeSelectField,
  )

  return {
    typeName,
    value,
    subLabel,
    mainLabel,
    modeLabel,
  }
}
